// 待办总览's project view. Renders top-level groups as clickable cards.
// Each card → /group/:id (a separate page that shows subgroups + 待办).
//
// If a group has subgroups (e.g., 门店管理 has RH/DT/NY), the card shows
// "N 个子分类 · M 待办". Otherwise it shows "N 待办 · K 完成".

import { navigate } from '../lib/routing.js';
import { UNGROUPED_ID, UNGROUPED_GROUP, ungroupedTasks } from '../lib/groups.ts';

export const ProjectView = ({ tasks, groups }) => {
  if (!groups || groups.length === 0) {
    return (
      <div className="vava-card empty-state">
        <div className="emoji">📋</div>
        <div className="title">还没有分类</div>
        <div className="sub">管理员可在数据库添加 groups 来组织待办</div>
      </div>
    );
  }

  const topGroups = groups
    .filter(g => !g.parentId)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  // Catch-all: tasks with no (valid) group are invisible under the real cards
  // because every card filters on groupId. Append an "未分类" card so they're
  // surfaced + navigable instead of silently dropping out of 待办总览.
  const ungrouped = ungroupedTasks(tasks || [], groups);
  const cards = ungrouped.length > 0 ? [...topGroups, UNGROUPED_GROUP] : topGroups;

  return (
    <div className="group-card-grid">
      {cards.map(g => {
        const isUngroupedCard = g.id === UNGROUPED_ID;
        const subgroups = isUngroupedCard ? [] : groups.filter(x => x.parentId === g.id);
        const subIds = new Set([g.id, ...subgroups.map(s => s.id)]);

        // Tasks under this top group (any sub, or direct). The catch-all card
        // collects every group-less task instead of matching on groupId.
        const tasksInGroup = isUngroupedCard ? ungrouped : tasks.filter(t => subIds.has(t.groupId));
        // 待办 = top-level parents (parentId is null), even if they sit in a subgroup
        const mainTasks = tasksInGroup.filter(t => t.parentId === null);
        const allSubs = tasksInGroup.filter(t => t.parentId !== null);
        const doneSubs = allSubs.filter(t => t.status === '已完成').length;

        return (
          <button key={g.id} className="group-card"
                  onClick={() => navigate('/group/' + g.id)}
                  aria-label={`${g.name} 分类`}>
            <div className="group-card-head">
              <span className="group-card-icon" aria-hidden="true">{g.icon || '📁'}</span>
              <span className="group-card-name">{g.name}</span>
              {subgroups.length > 0 && (
                <span className="group-card-subs">{subgroups.length} 子组</span>
              )}
            </div>
            {g.description && <div className="group-card-desc">{g.description}</div>}
            <div className="group-card-meta">
              <span><strong className="mono">{mainTasks.length}</strong> 待办</span>
              <span>·</span>
              <span className="mono">{doneSubs}/{allSubs.length} 完成</span>
              {subgroups.length > 0 && (
                <>
                  <span>·</span>
                  <span className="group-card-sub-list">
                    {subgroups.slice(0, 3).map(s => s.name).join(' / ')}
                    {subgroups.length > 3 ? '…' : ''}
                  </span>
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};
